/**
 * Helpers para supabase.functions.invoke — extrai mensagens do body JSON
 * quando o SDK retorna apenas "Edge Function returned a non-2xx status code".
 */

type InvokeErrorLike = {
    message?: string;
    context?: Response;
};

const SESSION_AUTH_PATTERNS = [
    'jwt',
    'session_not_found',
    'session not found',
    'invalid claim',
    'bad_jwt',
    'refresh token',
    'usuário não autenticado',
    'faça login novamente',
    'token ausente',
    'token malformado',
    'mfa',
    'aal2',
];

function readPayloadError(data: unknown): string | undefined {
    if (!data || typeof data !== 'object' || !('error' in data)) return undefined;
    const err = (data as { error: unknown }).error;
    if (err == null || err === '') return undefined;
    return String(err);
}

/** Mensagem de erro do body da Edge Function (quando o SDK só diz non-2xx). */
export async function readFunctionsInvokeErrorMessage(error: unknown): Promise<string | undefined> {
    if (!error || typeof error !== 'object') return undefined;
    return readContextError(error as InvokeErrorLike);
}

export function readFunctionsInvokeHttpStatus(error: unknown): number | null {
    if (!error || typeof error !== 'object') return null;
    return readContextStatus(error as InvokeErrorLike);
}

async function readContextError(error: InvokeErrorLike): Promise<string | undefined> {
    const ctx = error.context;
    if (!ctx) return undefined;

    if (typeof ctx.json === 'function') {
        try {
            const body = await ctx.clone().json();
            const fromJson = readPayloadError(body);
            if (fromJson) return fromJson;
        } catch {
            /* try text below */
        }
    }

    if (typeof ctx.text === 'function') {
        try {
            const text = await ctx.clone().text();
            if (!text) return undefined;
            try {
                return readPayloadError(JSON.parse(text));
            } catch {
                return text.slice(0, 240);
            }
        } catch {
            return undefined;
        }
    }

    return undefined;
}

function readContextStatus(error: InvokeErrorLike): number | null {
    const status = error.context?.status;
    return typeof status === 'number' && Number.isFinite(status) ? status : null;
}

function looksLikeSessionAuthError(message: string): boolean {
    const lower = message.toLowerCase();
    return SESSION_AUTH_PATTERNS.some((p) => lower.includes(p));
}

function messageForHttpStatus(status: number, fallback: string): string {
    if (status === 403) return 'Sem permissão para esta operação.';
    if (status === 401) return fallback;
    return fallback;
}

/**
 * Converte { data, error } do invoke em Error com mensagem amigável.
 */
export async function throwFunctionsInvokeError(
    data: unknown,
    error: unknown,
    fallback = 'Não foi possível completar a operação. Tente novamente.',
): Promise<never> {
    const payloadError = readPayloadError(data);
    if (payloadError) throw new Error(payloadError);

    if (error && typeof error === 'object') {
        const invokeError = error as InvokeErrorLike;
        const contextError = await readContextError(invokeError);
        if (contextError) throw new Error(contextError);

        const status = readContextStatus(invokeError);
        const msg = invokeError.message || '';

        if (status === 401 || status === 403) {
            // 401/403 de integração (ex.: token Google expirado) ≠ sessão Supabase
            throw new Error(messageForHttpStatus(status, fallback));
        }

        if (/Edge Function returned a non-2xx|FunctionsHttpError|FunctionsRelayError/i.test(msg)) {
            throw new Error(fallback);
        }

        if (msg && looksLikeSessionAuthError(msg)) {
            throw new Error('Sessão expirada. Faça login novamente.');
        }

        if (msg) throw new Error(msg);
    }

    throw new Error(fallback);
}
